'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback } from 'react'

import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import type { Category } from '@/lib/courses/data'
import {
  ageGroupLabels,
  difficultyLabels,
  type CourseAgeGroup,
  type CourseDifficulty,
} from '@/lib/courses/types'

interface CourseFiltersProps {
  categories: Category[]
  selected: {
    category?: string
    difficulty?: CourseDifficulty
    ageGroup?: CourseAgeGroup
  }
}

export function CourseFilters({ categories, selected }: CourseFiltersProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const updateParam = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString())
      if (value === 'all') {
        params.delete(key)
      } else {
        params.set(key, value)
      }
      router.push(`${pathname}?${params.toString()}`)
    },
    [pathname, router, searchParams],
  )

  const DIFFICULTIES: CourseDifficulty[] = ['beginner', 'intermediate', 'advanced']
  const AGE_GROUPS: CourseAgeGroup[] = ['all', 'child', 'teen', 'adult']

  return (
    <aside aria-label="Filteri" className="flex flex-col gap-6">
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-semibold">Kategorija</legend>
        <RadioGroup
          value={selected.category ?? 'all'}
          onValueChange={(v) => updateParam('category', v)}
          className="flex flex-col gap-2"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="all" id="cat-all" />
            <Label htmlFor="cat-all" className="text-sm font-normal">
              Sve kategorije
            </Label>
          </div>
          {categories.map((cat) => (
            <div key={cat.slug} className="flex items-center gap-2">
              <RadioGroupItem value={cat.slug} id={`cat-${cat.slug}`} />
              <Label htmlFor={`cat-${cat.slug}`} className="text-sm font-normal">
                {cat.emoji} {cat.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-semibold">Težina</legend>
        <RadioGroup
          value={selected.difficulty ?? 'all'}
          onValueChange={(v) => updateParam('difficulty', v)}
          className="flex flex-col gap-2"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="all" id="diff-all" />
            <Label htmlFor="diff-all" className="text-sm font-normal">
              Svi nivoi
            </Label>
          </div>
          {DIFFICULTIES.map((d) => (
            <div key={d} className="flex items-center gap-2">
              <RadioGroupItem value={d} id={`diff-${d}`} />
              <Label htmlFor={`diff-${d}`} className="text-sm font-normal">
                {difficultyLabels[d]}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-semibold">Uzrast</legend>
        <RadioGroup
          value={selected.ageGroup ?? 'all'}
          onValueChange={(v) => updateParam('ageGroup', v)}
          className="flex flex-col gap-2"
        >
          {AGE_GROUPS.map((ag) => (
            <div key={ag} className="flex items-center gap-2">
              <RadioGroupItem value={ag} id={`age-${ag}`} />
              <Label htmlFor={`age-${ag}`} className="text-sm font-normal">
                {ageGroupLabels[ag]}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>
    </aside>
  )
}
